import { useState } from "react";
import { toast } from "sonner";
import { BadgeCheck } from "lucide-react";
import PartnerGate from "./Gate";
import { Button } from "@/components/ui/button";
import { inr } from "@/components/bits";
import api, { errMsg, fmtDate } from "@/lib/api";
import { payWithPayU } from "@/lib/payu";

export default function Subscription() {
  const [payBusy, setPayBusy] = useState("");

  const buy = async (kind, refId) => {
    setPayBusy(refId);
    try {
      const { data } = await api.post("/payments/checkout", {
        kind, ref_id: refId, cycle: "yearly", origin_url: window.location.origin,
      });
      payWithPayU(data.payu_url, data.params);
    } catch (e) {
      toast.error(errMsg(e));
      setPayBusy("");
    }
  };

  return (
    <PartnerGate>
      {(overview) => {
        const sub = overview.subscription || {};
        const daysLeft = sub.expires_at
          ? Math.max(0, Math.ceil((new Date(sub.expires_at) - Date.now()) / 86400000))
          : 0;
        return (
          <div data-testid="partner-subscription" className="space-y-6">
            <div className="bg-white border border-border/60 rounded-xl p-6" data-testid="current-plan-card">
              <div className="flex flex-wrap items-center justify-between gap-4">
                <div>
                  <p className="text-xs text-muted-foreground">Current plan</p>
                  <h3 className="font-heading font-bold text-xl text-[#0B1B3D] mt-1" data-testid="current-plan-name">{sub.plan_name}</h3>
                  <p className="text-xs text-muted-foreground mt-1">
                    {sub.scope === "global" ? "Domestic + International openings" : "Domestic (India) openings"} ·
                    started {fmtDate(sub.started_at)} · expires {fmtDate(sub.expires_at)}
                  </p>
                </div>
                <div className="text-right">
                  <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-3 py-1">
                    <BadgeCheck className="w-3.5 h-3.5" /> Active
                  </span>
                  <p className="text-xs text-muted-foreground mt-2" data-testid="days-left">{daysLeft} days remaining</p>
                </div>
              </div>
            </div>

            <div className="grid lg:grid-cols-2 gap-6">
              <div className="bg-white border border-border/60 rounded-xl p-6" data-testid="renew-card">
                <h3 className="font-heading font-semibold text-[#0B1B3D] mb-4">Renew or change plan</h3>
                <div className="space-y-3">
                  {(overview.plans || []).map((p) => (
                    <div key={p.id} className="flex items-center justify-between gap-3 bg-[#F8FAFC] border border-border/60 rounded-lg px-4 py-3" data-testid={`renew-plan-${p.id}`}>
                      <div>
                        <p className="text-sm font-semibold text-[#0B1B3D]">{p.name}</p>
                        <p className="text-xs text-muted-foreground">{inr(p.price_yearly)} + {p.gst_pct}% GST / year</p>
                      </div>
                      <Button
                        size="sm" className="rounded-full bg-[#0B1B3D]" disabled={!!payBusy}
                        onClick={() => buy("subscription", p.id)} data-testid={`renew-${p.id}`}
                      >
                        {sub.plan_id === p.id ? "Renew" : "Switch"}
                      </Button>
                    </div>
                  ))}
                </div>
              </div>

              <div className="space-y-6">
                <div className="bg-white border border-border/60 rounded-xl p-6" data-testid="franchise-card">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-heading font-semibold text-[#0B1B3D]">Franchise Partnership</h3>
                    {overview.franchise?.status === "active" ? (
                      <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-3 py-1" data-testid="franchise-active-badge">
                        <BadgeCheck className="w-3.5 h-3.5" /> Active
                      </span>
                    ) : (
                      <span className="text-xs font-semibold text-slate-500 bg-slate-100 rounded-full px-3 py-1" data-testid="franchise-inactive-badge">Not activated</span>
                    )}
                  </div>
                  {overview.franchise?.status === "active" ? (
                    <p className="text-sm text-muted-foreground">
                      Activated on {fmtDate(overview.franchise.paid_at)}. Every placement payout earns a{" "}
                      <span className="font-semibold text-emerald-700">{overview.franchise.boost_pct}% franchise boost</span>.
                    </p>
                  ) : (
                    <>
                      <p className="text-sm text-muted-foreground mb-3">
                        One-time {inr(overview.franchise?.fee ?? 0)} + GST —{" "}
                        <span className="font-semibold text-[#0B1B3D]">{overview.franchise?.boost_pct}% boost</span> on every placement payout, forever.
                      </p>
                      {overview.is_member ? (
                        <p className="text-xs text-muted-foreground" data-testid="franchise-member-note">Only the franchise owner can activate the package.</p>
                      ) : (
                        <Button size="sm" onClick={() => buy("franchise", overview.partner.id)} disabled={!!payBusy} className="rounded-full bg-[#0B1B3D]" data-testid="buy-franchise-btn">
                          Activate Franchise Package
                        </Button>
                      )}
                    </>
                  )}
                </div>

                <div className="bg-white border border-border/60 rounded-xl p-6" data-testid="tier-card">
                  <h3 className="font-heading font-semibold text-[#0B1B3D] mb-3">Payout Tier</h3>
                  {overview.payout_tier?.current ? (
                    <>
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-semibold text-[#0B1B3D] bg-[#F5A623]/10 border border-[#F5A623]/30 rounded-full px-3 py-1" data-testid="current-tier">
                          {overview.payout_tier.current.label} tier
                        </span>
                        <span className="text-xs text-muted-foreground">{overview.placements} placements</span>
                      </div>
                      <p className="text-sm text-muted-foreground">
                        {inr(overview.payout_tier.current.prepaid_amount)} per prepaid joining · {overview.payout_tier.current.postpaid_pct}% of CTC postpaid
                      </p>
                      {overview.payout_tier.next ? (
                        <p className="text-xs text-muted-foreground mt-3 bg-[#F8FAFC] border border-border/60 rounded-lg px-3 py-2" data-testid="next-tier">
                          {overview.payout_tier.next.min_placements - overview.placements} more placement{overview.payout_tier.next.min_placements - overview.placements > 1 ? "s" : ""} to reach{" "}
                          <span className="font-semibold text-[#0B1B3D]">{overview.payout_tier.next.label}</span>
                        </p>
                      ) : (
                        <p className="text-xs text-emerald-700 mt-3 font-semibold" data-testid="top-tier">You are on the highest payout tier.</p>
                      )}
                    </>
                  ) : (
                    <p className="text-sm text-muted-foreground" data-testid="no-tiers">Standard payout rates apply.</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        );
      }}
    </PartnerGate>
  );
}
